import * as React from "react";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridRowSelectionModel,
  GridSortModel,
  GridPaginationModel,
  GridFilterModel,
  GridEventListener,
  GridColDef,
  GridValidRowModel,
} from "@mui/x-data-grid";
import { GridPagination, useGridApiContext } from "@mui/x-data-grid";
import MuiPagination from "@mui/material/Pagination";
import { Box, Button } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { TablePaginationProps } from "@mui/material/TablePagination";
import { ExportExcelButton } from "../ExportExcelButton";
import { useDynamicHeight } from "./useDynamicHeight";
import { ExportFlyerButton } from "../ExportFlyerButton";
import { DataTable } from "./styled";
import HomeRepairServiceIcon from "@mui/icons-material/HomeRepairService";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useHeaderFilterModel } from "./headerFilters/useHeaderFilterModel";
import { useColumnsWithHeaderFilters } from "./headerFilters/withHeaderFilters";
import { ActiveFilterChips } from "./headerFilters/ActiveFilterChips";
import { ListLoadFailedAlert } from "./ListLoadFailedAlert";

const HEADER_FILTER_COLUMN_HEIGHT = 96;

function Pagination({
  page,
  onPageChange,
  className,
}: Pick<TablePaginationProps, "page" | "onPageChange" | "className">) {
  const apiRef = useGridApiContext();
  return (
    <MuiPagination
      color="primary"
      className={className}
      count={Math.ceil(
        apiRef.current.state.rows.totalRowCount /
          apiRef.current.state.pagination.paginationModel.pageSize,
      )}
      page={page + 1}
      onChange={(event, newPage) => {
        onPageChange(
          event as React.MouseEvent<HTMLButtonElement> | null,
          newPage - 1,
        );
      }}
      data-testid="pagination-test-id"
    />
  );
}
function CustomPagination(
  props: React.JSXElementConstructor<any> | null | undefined,
) {
  return (
    <GridPagination
      ActionsComponent={Pagination}
      {...props}
      data-testid="custompagination-test-id"
    />
  );
}

interface ListTableQueryPaginationProps {
  columns?: GridColDef[];
  rows?: readonly GridValidRowModel[];
  rowBuffer?: number;
  isLoading?: boolean;
  page?: number;
  perPage?: number;
  total?: number;
  handleRowSelection?: (value: string[] | GridRowSelectionModel) => void;
  rowSelectionModel?: GridRowSelectionModel;
  paginationModel?: GridPaginationModel;
  sortModel?: GridSortModel;
  className?: string;
  onRowClick?: GridEventListener<"rowClick">;
  onExcelExportClick?: () => void;
  onImportExcelClick?: () => void;
  onTableChange: (
    gridPaginationModel: GridPaginationModel,
    gridSortModel: GridSortModel,
  ) => void;
  dynamicHeightAdjuster?: number;
  onGenerateFlyer?: () => void;
  onDownloadFLyer?: () => void;
  onToolsClick?: () => void;
  onStatisticsClick?: () => void;
  onFilterModelChange?: (filterModel: GridFilterModel) => void;
  filterModel?: GridFilterModel;
  /** The last fetch failed; warn that the rows shown may be stale. */
  loadFailed?: boolean;
  /**
   * Defaults to "server": rows arrive already sorted, so the grid must not
   * re-sort them. Use "client" only when the current data source can't sort.
   */
  sortingMode?: "client" | "server";
}
export function ListTableQueryPagination({
  rows,
  rowBuffer = 100,
  columns,
  isLoading,
  total,
  paginationModel,
  handleRowSelection,
  rowSelectionModel: controlledRowSelectionModel,
  className,
  onRowClick,
  sortModel,
  onExcelExportClick,
  onTableChange,
  dynamicHeightAdjuster,
  onGenerateFlyer,
  onDownloadFLyer,
  onImportExcelClick,
  onToolsClick,
  onStatisticsClick,
  onFilterModelChange,
  filterModel,
  loadFailed,
  sortingMode = "server",
}: ListTableQueryPaginationProps) {
  const { height } = useDynamicHeight(dynamicHeightAdjuster);
  const headerFilterEnabled = Boolean(onFilterModelChange);

  const filterApi = useHeaderFilterModel({
    filterModel,
    onFilterModelChange: headerFilterEnabled ? onFilterModelChange : undefined,
  });

  const enhancedColumns = useColumnsWithHeaderFilters(
    columns ?? [],
    headerFilterEnabled ? filterApi : null,
    headerFilterEnabled,
  );

  const [rowCountState, setRowCountState] = React.useState(total || 0);
  const [internalRowSelectionModel, setInternalRowSelectionModel] =
    React.useState<GridRowSelectionModel>([]);

  const rowSelectionModel =
    controlledRowSelectionModel ?? internalRowSelectionModel;

  function CustomToolbar() {
    return (
      <GridToolbarContainer data-testid="listtable-test-id">
        <GridToolbarColumnsButton />
        {!headerFilterEnabled && <GridToolbarFilterButton />}
        <GridToolbarDensitySelector />
        {onExcelExportClick ? (
          <ExportExcelButton
            onExportClick={onExcelExportClick}
            onImportExcelClick={onImportExcelClick}
          />
        ) : (
          <></>
        )}
        {onGenerateFlyer ? (
          <ExportFlyerButton
            onGenerateFlyer={onGenerateFlyer}
            onDownloadFLyer={onDownloadFLyer}
          />
        ) : (
          <></>
        )}

        {onToolsClick ? (
          <Button
            id="tools-button"
            onClick={onToolsClick}
            sx={{ textTransform: "capitalize" }}
            data-testid="tools-button"
            startIcon={<HomeRepairServiceIcon></HomeRepairServiceIcon>}
          >
            Tools
          </Button>
        ) : (
          <></>
        )}

        {onStatisticsClick ? (
          <Button
            id="statistics-button"
            onClick={onStatisticsClick}
            sx={{ textTransform: "capitalize" }}
            data-testid="statistics-button"
            startIcon={<AssessmentIcon></AssessmentIcon>}
          >
            Statistics
          </Button>
        ) : (
          <></>
        )}
      </GridToolbarContainer>
    );
  }

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      total !== undefined ? total : prevRowCountState,
    );
  }, [total, setRowCountState]);

  React.useEffect(() => {
    if (controlledRowSelectionModel === undefined) {
      handleRowSelection && handleRowSelection(rowSelectionModel);
    }
  }, [controlledRowSelectionModel, handleRowSelection, rowSelectionModel]);

  const grid = (
    <DataTable
      rowBuffer={rowBuffer}
      columnBuffer={20}
      columns={enhancedColumns}
      rows={rows ? rows : []}
      slots={{ toolbar: CustomToolbar, pagination: CustomPagination }}
      loading={isLoading !== undefined ? isLoading : false}
      checkboxSelection
      disableRowSelectionOnClick
      paginationMode="server"
      sortingMode={sortingMode}
      disableColumnFilter={headerFilterEnabled}
      disableColumnMenu={headerFilterEnabled}
      className={headerFilterEnabled ? "with-header-filters" : undefined}
      columnHeaderHeight={
        headerFilterEnabled ? HEADER_FILTER_COLUMN_HEIGHT : undefined
      }
      getRowClassName={(params) =>
        params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
      }
      rowCount={rowCountState}
      pageSizeOptions={Array.from(Array(10).keys())?.map(
        (value) => (value + 1) * 10,
      )}
      paginationModel={
        paginationModel
          ? { ...paginationModel, page: paginationModel.page - 1 }
          : { page: 0, pageSize: 10 }
      }
      onPaginationModelChange={(model: GridPaginationModel) => {
        if (headerFilterEnabled) {
          filterApi.flushPending();
        }
        const clonedObj = { ...model };
        clonedObj.page = model.page + 1;
        onTableChange && onTableChange(clonedObj, sortModel || []);
      }}
      sortModel={sortModel || []}
      onSortModelChange={(sortModel: GridSortModel) => {
        if (headerFilterEnabled) {
          filterApi.flushPending();
        }
        const clonedObj = {
          ...(paginationModel || { page: 1, pageSize: 10 }),
        };
        onTableChange && onTableChange(clonedObj, sortModel);
      }}
      getRowId={(row) => row.id}
      onRowSelectionModelChange={(newRowSelectionModel) => {
        if (controlledRowSelectionModel === undefined) {
          setInternalRowSelectionModel(newRowSelectionModel);
        }
        handleRowSelection && handleRowSelection(newRowSelectionModel);
      }}
      onRowClick={onRowClick}
      rowSelectionModel={rowSelectionModel}
    />
  );

  const gridWithLocalization = headerFilterEnabled ? (
    <LocalizationProvider dateAdapter={AdapterDayjs}>{grid}</LocalizationProvider>
  ) : (
    grid
  );

  return (
    <Box
      sx={{
        height: `${height}px`,
        width: "100%",
        display: "flex",
        flexDirection: "column",
      }}
      className={className}
      data-testid="listtable-test-id"
    >
      <ListLoadFailedAlert show={loadFailed} />
      {headerFilterEnabled && (
        <ActiveFilterChips
          filterModel={filterApi.localModel}
          columns={columns ?? []}
          onRemove={filterApi.clearItem}
          onClearAll={filterApi.clearAll}
        />
      )}
      <Box sx={{ flex: "1 1 auto", minHeight: 0, width: "100%" }}>
        {gridWithLocalization}
      </Box>
    </Box>
  );
}
